import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import Skeleton from '@mui/material/Skeleton';
// @mui
import {
  Grid,
  Container,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Card,
  CardHeader,
  Box,
  Divider,
} from '@mui/material';
// components
import { forEach } from 'lodash';
import axiosInstance from '../utils/axiosInstance';
// sections
import {
  AppRtspList,
  AppWidgetSummary,
  AppCameraStorage,
  SensorTable
} from '../sections/@dashboard/app';

// ----------------------------------------------------------------------

export default function DashboardAppPage() {
  const { adaptorType } = window;
  const hostList = useSelector((state) => state.app.hostList);
  const cameraList = useSelector((state) => state.app.cameraList);
  const [vstRecordSize, setVstRecordSize] = useState(null);
  const [forceRefresh, setForcedRefresh] = useState(true);
  const [vstDevice, setVstDevice] = useState(
    hostList.length > 0 ? hostList[0] : null
  );
  const [offlineCameraCount, setOfflineCameraCount] = useState(0);

  // get total record size
  useEffect(() => {
    let total = 0;
    const getTotalRecordSize = () => {
      forEach(hostList, (host) => {
        axiosInstance
          .get(`${window.location.protocol}//${host}/api/v1/storage/size`)
          .then((response) => {
            total += Number(response.data.total.sizeInMb || 0);
            setVstRecordSize(total);
          })
          .catch((error) => {
            console.log('Failed to get VST record size', error);
          });
      });
    };
    getTotalRecordSize();
  }, [hostList, forceRefresh]);

  // get cameras which are not online
  useEffect(() => {
    let total = 0;
    const getofflineCameraCount = () => {
      forEach(hostList, (host) => {
        axiosInstance
          .get(`${window.location.protocol}//${host}/api/v1/sensor/status`)
          .then((response) => {
            const cameraStatusList = response.data;
            forEach(cameraStatusList, (cameraStatus) => {
              if (cameraStatus.state === 'offline') {
                total += 1;
                setOfflineCameraCount(total);
              }
            });
          })
          .catch((error) => {
            console.log('Failed to get device status', error);
          });
      });
    };
    getofflineCameraCount();
  }, [hostList, forceRefresh]);

  const handleVstDeviceChange = (e) => {
    setVstDevice(e.target.value);
  };

  window.refreshDashboard = () => {
    console.log("Forced refresh")
    setForcedRefresh(false);
    setTimeout(() => {
      setForcedRefresh(true);
    }, 500);
  }

  return (
    <>
      <Helmet>
        <title> Dashboard | VST UI </title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          Hi, Welcome back
        </Typography>


        <Grid container spacing={3}>

          {
            forceRefresh &&
            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary
                title={`${adaptorType} devices`}
                total={hostList.length}
                icon={'mdi:memory'}
              />
            </Grid>
          }
          {
            !forceRefresh &&
            <Grid item xs={12} sm={6} md={3}>
              <Skeleton variant="rounded" height={228} />
            </Grid>
          }
          {
            forceRefresh &&
            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary
                title="Cameras"
                total={cameraList.length}
                color="info"
                icon={'mdi:cctv'}
              />
            </Grid>
          }
          {
            !forceRefresh &&
            <Grid item xs={12} sm={6} md={3}>
              <Skeleton variant="rounded" height={228} />
            </Grid>
          }
          {
            forceRefresh &&
            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary
                title="Record size"
                total={Number(vstRecordSize) * 1024 * 1024} // bytes
                color="warning"
                icon={'mdi:sd'}
              />
            </Grid>
          }
          {
            !forceRefresh &&
            <Grid item xs={12} sm={6} md={3}>
              <Skeleton variant="rounded" height={228} />
            </Grid>
          }
          {
            forceRefresh &&
            <Grid item xs={12} sm={6} md={3}>
              <AppWidgetSummary
                title="Offline cameras"
                total={offlineCameraCount}
                color="error"
                icon={'mdi:cctv-off'}
              />
            </Grid>
          }
          {
            !forceRefresh &&
            <Grid item xs={12} sm={6} md={3}>
              <Skeleton variant="rounded" height={228} />
            </Grid>
          }


          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>
          {
            forceRefresh &&
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title={`${adaptorType} devices`}
                  subheader={`Select ${adaptorType} device`}
                />
                <Box sx={{ p: 3, pb: 1 }} dir="ltr">
                  <FormControl>
                    <RadioGroup
                      row
                      name="vst-device-radio-button"
                      value={vstDevice}
                      onChange={handleVstDeviceChange}
                    >
                      {hostList.map((host) => (
                        <FormControlLabel
                          value={host}
                          control={<Radio />}
                          label={`${adaptorType} ${host}`}
                          key={host}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Card>
            </Grid>
          }
          {
            !forceRefresh &&
            <Grid item xs={12}>
              <Card>
                <Skeleton variant="rounded" height={100} />
              </Card>
            </Grid>
          }


          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>
          {
            forceRefresh &&
            <Grid item xs={12}>
              <SensorTable host={vstDevice} />
            </Grid>

          }
          {
            !forceRefresh &&
            <Grid item xs={12}>
              <Card>
                <Skeleton variant="rounded" height={300} />
              </Card>
            </Grid>
          }

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>
          {
            forceRefresh &&
            <Grid item xs={12} md={12} lg={12}>
              <AppRtspList
                title="RTSP URLs"
                subheader="Click on entries to copy RTSP URL"
                host={vstDevice}
                rowCount={5}
              />
            </Grid>
          }
          {
            !forceRefresh &&
            <Grid item xs={12}>
              <Card>
                <Skeleton variant="rounded" height={300} />
              </Card>
            </Grid>
          }


          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>
          {
            forceRefresh &&
            adaptorType !== 'mms' && (
              <Grid item xs={12} md={12} lg={12}>
                <AppCameraStorage
                  key={vstDevice}
                  host={vstDevice}
                  title="Camera record size"
                  subheader={`Storage consumed by each camera on VST ${vstDevice}`}
                />
              </Grid>
            )
          }
          {
            !forceRefresh && adaptorType !== 'mms' &&
            <Grid item xs={12}>
              <Card>
                <Skeleton variant="rounded" height={300} />
              </Card>
            </Grid>
          }

        </Grid>
      </Container>
    </>
  );
}
